import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { delay, filter, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, noop, Observable, of, Subject } from 'rxjs';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { MatSort } from '@angular/material/sort';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatDrawer } from '@angular/material/sidenav';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { AccountService } from 'app/core';
import { FieldDef, QueryConfig, SavedFilter, SavedView } from 'app/core/models';
import { FileFormat, RelatedToType } from 'app/shared/enums';
import { CustomObject, CustomObjectValue } from '../custom-objects.types';
import { ChangeType, OriginalStates, TableSortState } from 'app/shared/models';
import { CustomObjectActions } from '../state/actions';
import { CustomObjectState } from '../state/state';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { ObjectsManagerActions } from 'app/modules/settings/objects-manager/state/actions';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { GraphqlResultPageInfo } from 'app/shared/models/graphql';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { AccountState } from 'app/state/account/state';
import { FeatureFlag } from 'app/core/enums';
import UrlUtils from 'app/shared/Utils/url.utils';
import { isArraysEqual } from 'app/shared/Utils/common.utils';
import { TsRxjsOperators } from 'app/shared/Utils/custom-rxjs-operators.utils';
import {
  Dictionary,
  FilterListItemChangeArgs,
  FilterListItemModel,
  FilterListItemValueModel,
  RedirectItem,
  TsButtonComponent,
  TsOverlayComponent,
  TsPaginatorChangeEvent,
  TsSnackbarService,
  TsTableService,
} from 'tsui';
import { LookupService } from '@services/lookup.service';
import { TsFilterUtilsService } from '@services/ts-filter-utils.service';
import { FormControl } from '@angular/forms';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { FieldDefsState } from '@state/fields/state';
import { Project } from 'app/modules/projects/projects.types';
import { SortChangedEvent } from 'ag-grid-enterprise';
import { hasLinkedField } from '../custom-objects.functions';
import { removeArchivedFieldsFromTableDefinition } from '@shared/functions/fields-functions';
import { PermissionUtils } from '@core/utils/permission.utils';
import { UserState } from '@state/user/state';
import { TsMenuItem } from 'tsui/lib/ts-menu/ts-menu.component';
import { SavedViewUtilsService } from '@services/saved-view-utils.service';

type GraphqlResult = { rows: any[]; pageInfo?: GraphqlResultPageInfo };

@Component({
  selector: 'custom-objects-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss', '../../../../shared/styles/ts-page-header-list.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CustomObjectsListComponent extends OnDestroyMixin implements OnInit {
  customObject: CustomObject;
  customObjectValue: CustomObjectValue;
  customObjectValues: CustomObjectValue[];
  customObjectValuesTableColumns: string[] = [];
  displayColumns: string[] = [];
  customObjectValuesCount: number;
  customObjectValuesCountMsgMapping: Dictionary<string>;
  filteredFieldDefs: FieldDef[] = [];
  customObjectOperation: 'create' | 'update';
  drawerOpened = false;
  data: Dictionary<any>;
  pageInfo: GraphqlResultPageInfo;
  pageSize = 20;
  queryFilters: Partial<QueryConfig.QueryFilters>;
  savedFilter: SavedFilter;
  templateId: number;
  hasNestedFiltersOn = false;
  typeFields = new BehaviorSubject<FilterListItemModel[]>([]);
  filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);
  customObjects = this.store.selectSnapshot(ObjectsManagerState.get);
  columnDefs: ColumnDef[] = [];
  actions: TableActions[] = [];
  relatedToRedirectConfig: Dictionary<RedirectItem> = {
    Contact: { route: (): Observable<string> => of('/contacts/:id') },
    Company: { route: (): Observable<string> => of('/companies/:id') },
    Property: { route: (): Observable<string> => of('/properties/:id') },
    Project: {
      deferred: true,
      route: (key?: number): Observable<string> => {
        return this.projectService.getProjectByIdBase(String(key)).pipe(
          untilComponentDestroyed(this),
          switchMap((project: Project) => of(`/projects/${project.pipeline_id}/${key}`)),
        );
      },
    } as RedirectItem,
  };
  searchInputControl: FormControl = new FormControl('');
  newTable: boolean = false;
  sortState: TableSortState = {
    active: 'name',
    direction: 'asc',
  };
  readonly savedView = signal<SavedView>(undefined);
  readonly isLoading = signal(false);
  viewManagementOptions: TsMenuItem[];

  get customObjectId(): number {
    return Number(
      this.activatedRoute.snapshot.paramMap.get('id') || this.activatedRoute.snapshot.paramMap.get('customObjectId'),
    );
  }

  get filterCounter(): number {
    return this.filterValueList?.value?.length || 0;
  }

  get relatedToType(): string {
    return this.activatedRoute.snapshot.queryParams?.related_to_type;
  }

  get relatedToId(): number {
    return Number(this.activatedRoute.snapshot.queryParams?.related_to_id);
  }

  private get isFromRoot(): boolean {
    return !!this.activatedRoute.parent.snapshot.data?.fromRoot;
  }

  private get fileName(): string {
    return `${this.customObject.name.replace(/ /g, '').substring(0, 10)}-${this.customObject.id}`;
  }

  private get fjsonFilterAsObject(): any[][] {
    return JSON.parse(this.queryFilters.fjson || '[]');
  }

  private get customObjectSavedViewKey(): string {
    return `customObjectSavedView-${this.customObjectId}`;
  }

  @ViewChild('drawer') matDrawer: MatDrawer;
  @ViewChild(MatSort) private sort: MatSort;
  @ViewChild('fieldDefsPanelOrigin') private fieldDefsPanelOrigin: TsButtonComponent;
  @ViewChild('fieldDefsPanel') private fieldDefsPanel: TemplateRef<any>;
  @ViewChild('filtersOverlayBox') overlayBox: TsOverlayComponent;

  private getById$: Observable<CustomObject> = inject(Store).select(ObjectsManagerState.getById);
  private graphqlResults$: Observable<GraphqlResult> = inject(Store).select(CustomObjectState.graphqlResults);
  private getValueById$: Observable<CustomObjectValue> = inject(Store).select(CustomObjectState.getValueById);

  private changeTriggers: Set<string> = new Set();
  private originalStates: OriginalStates;
  private columnsDebouncer = new Subject<string[]>();
  private fieldDefs: FieldDef[] = [];
  private fieldDefsPanelOverlayRef: OverlayRef;
  private fieldsDefsPanelTemplatePortal: TemplatePortal;
  private customObjectClickActions = {
    [RelatedToType.Project]: this.redirectToProject,
    [RelatedToType.Property]: this.redirectToProperty,
    [RelatedToType.Company]: this.redirectToCompany,
    [RelatedToType.Contact]: this.redirectToContact,
  };

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly accountService: AccountService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly projectService: ProjectsService,
    private readonly overlay: Overlay,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly confirmService: ConfirmService,
    public readonly lookupService: LookupService,
    private readonly snackBar: TsSnackbarService,
    private readonly tsFilterUtilsService: TsFilterUtilsService,
    private readonly savedViewUtilsService: SavedViewUtilsService,
  ) {
    super();
    this.customObject = this.activatedRoute.snapshot.data.customObject;
    this.viewManagementOptions = this.savedViewUtilsService.getViewManagementMenuOptions(
      this.savedView,
      this.customObject?.name,
      this.isLoading,
      this.customObjectId,
    );

    this.customObjectValuesCount = 0;
    this.customObjectValuesCountMsgMapping = {
      '=0': 'No values',
      '=1': 'value',
      other: 'values',
    };

    this.hasNestedFiltersOn = this.store
      .selectSnapshot(AccountState.getAccount)
      ?.features.includes(FeatureFlag.NESTED_FILTERS);

    this.relatedToRedirectConfig = this.store
      .selectSnapshot(ObjectsManagerState.get)
      .filter((x) => x.id)
      .reduce((p, c) => {
        return { ...p, [c.name]: { route: (): Observable<string> => of(`/objects/${c.id}/:id/details`) } };
      }, this.relatedToRedirectConfig);

    this.getValueById$.pipe(untilComponentDestroyed(this)).subscribe((value) => {
      this.customObjectValue = value;
    });
  }

  ngOnInit(): void {
    this.setupSubscriptions();

    this.customObjectOperation = null;

    this.columnsDebouncer
      .asObservable()
      .pipe(untilComponentDestroyed(this), TsRxjsOperators.memoizedDebounceTime(500))
      .subscribe((newColumns) => {
        this.openTrackStateSnackbar('columns');
        this.displayColumns = [...newColumns.filter((item) => item !== 'actions')];
        this.renderSavedView();
        this.handleCustomObjectValuesTableColumns(this.displayColumns);
        this.getCustomObjectValues();
      });

    this.searchInputControl.valueChanges
      .pipe(untilComponentDestroyed(this), TsRxjsOperators.memoizedDebounceTime(500))
      .subscribe((query) => {
        this.queryFilters = { ...this.queryFilters, q: query };
        this.getCustomObjectValues(false, 1);
      });

    this.closeSnackBarOnLeave();

    this.checkTable();
    this.getColDefs();
  }

  private setupSubscriptions(): void {
    this.activatedRoute.params
      .pipe(
        untilComponentDestroyed(this),
        tap(() => {
          this.fetchFieldDefs();
          this.refreshRouteData();
          this.getCustomObject();
          this.getCustomObjectValues();
        }),
      )
      .subscribe(() => {
        this.renderSavedView();
        this.changeDetectorRef.detectChanges();
      });

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(
          CustomObjectActions.CreateValue,
          CustomObjectActions.UpdateValue,
          CustomObjectActions.DeleteValue,
        ),
        tap(() => this.getCustomObjectValues()),
      )
      .subscribe(noop);

    this.getById$
      .pipe(
        untilComponentDestroyed(this),
        filter((customObject) => !!customObject),
        switchMap((customObject) => combineLatest([of(customObject), this.graphqlResults$, this.activatedRoute.data])),
        tap(([customObject, graphqlResults, data]: [CustomObject, GraphqlResult, Dictionary<any>]) => {
          this.customObject = customObject;
          this.viewManagementOptions = this.savedViewUtilsService.getViewManagementMenuOptions(
            this.savedView,
            this.customObject?.name,
            this.isLoading,
            this.customObjectId,
          );
          this.data = { customObjectId: this.customObjectId, ...data };
          this.handleCustomObjectValues(graphqlResults);
          this.handleCountMsgMapping();
        }),
      )
      .subscribe(() => this.changeDetectorRef.detectChanges());
  }

  private handleCustomObjectValues(customObjectValues: GraphqlResult): void {
    this.isLoading.set(true);
    if (customObjectValues?.rows?.length) {
      this.customObjectValues = customObjectValues.rows;
      this.customObjectValuesCount = customObjectValues.pageInfo.filteredCount;
      this.pageInfo = customObjectValues.pageInfo;
    } else {
      this.customObjectValues = [];
      this.customObjectValuesCount = 0;
      this.pageInfo = null;
    }
    this.isLoading.set(false);
  }

  private getCustomObject(): void {
    this.store.dispatch(new ObjectsManagerActions.GetById(this.customObjectId));
  }

  private columnsToFieldDefs(columns: string[]): FieldDef[] {
    return Array.from(
      columns
        .filter((item) => item !== 'actions')
        .reduce((acc: Set<FieldDef>, column: string) => {
          const splitValues = column.split('.');
          if (splitValues.length > 1) {
            // when related column cf_favorite_custom_a.cf_name is present
            // we need to add the parent field cf_favorite_custom_a because the field.meta.record_type is there.
            acc.add(this.fieldDefs.find((field) => field.name === splitValues[0]));
          }
          acc.add(this.fieldDefs.find((field) => field.name === column));
          return acc;
        }, new Set()),
    );
  }

  private getCustomObjectValues(toExport = false, page?: number): void {
    this.isLoading.set(true);
    let hiddenFilter = [];

    if (!Number.isNaN(this.relatedToId)) {
      const linkedFieldName = this.getLinkedField()?.name;

      if (!this.fjsonFilterAsObject.filter((x) => x.find((y) => y.includes(linkedFieldName))).length) {
        if (this.hasNestedFiltersOn) {
          hiddenFilter = [[`${linkedFieldName}.id`, 'in', [this.relatedToId]]];
        } else {
          hiddenFilter = [[linkedFieldName, 'in', [[this.relatedToId]]]];
        }
      }
    }

    this.store.dispatch(
      new CustomObjectActions.Graphql.Action(
        {
          connection: this.customObject.name,
          fieldDefs: this.hasNestedFiltersOn ? this.columnsToFieldDefs(this.displayColumns) : undefined,
          fields: this.displayColumns,
          filter: {
            fjson: this.queryFilters.fjson,
            query: this.queryFilters.q,
            pagination: { page: page || this.pageInfo?.currentPage || 1, size: this.pageSize },
            sort: [
              {
                field: this.sortState.active,
                order: this.sortState.direction,
              },
            ],
          },
          exportOptions: toExport
            ? { fileName: this.fileName, format: FileFormat.CSV, savedViewId: String(this.savedView().id) }
            : undefined,
        },
        hiddenFilter,
      ),
    );
    this.isLoading.set(false);
  }

  pageChanged(event: TsPaginatorChangeEvent): void {
    this.pageSize = event.pageSize;
    this.pageInfo = { ...this.pageInfo, pageSize: this.pageSize, currentPage: event.activePage };
    this.getCustomObjectValues();
  }

  private handleCountMsgMapping(): void {
    this.customObjectValuesCountMsgMapping = {
      '=0': this.customObject?.label ? `No ${this.customObject.label}` : 'No values',
      '=1': this.customObject?.name ? `${this.customObject.name}` : 'value',
      other: this.customObject?.label ? `${this.customObject.label}` : 'values',
    };
  }

  addCustomObjectValue(event: Event): void {
    this.customObjectValue = null;
    this.store
      .dispatch(new ObjectsManagerActions.GetById(this.customObject.id))
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        event.preventDefault();
        this.customObjectOperation = 'create';
        this.customObjectValue = this.getInitialCOValueData();
        this.changeDetectorRef.markForCheck();
        this.matDrawer.toggle();
      });
  }

  getObjectName(column): string {
    if (!column.name.includes('.')) {
      return '';
    }

    let result = column.name
      .replace(/cf_linked_/, '')
      .replace(/cf_/, '')
      .replace(/co_\d+_/, '')
      .split('.')[0]
      .split('_')
      .join(' ');
    return result;
  }

  private getContextCurrentRecordFormat(): string {
    const model = this.data[this.relatedToType.toLowerCase()];

    if (this.customObjectValue) {
      const searchField = this.customObjectValue.customObject.custom_fields.find(
        (x) => x.id === this.customObjectValue.customObject.search_custom_field_id,
      )?.name;

      return searchField
        ? `${this.customObjectValue.id}|${this.customObjectValue[searchField]}`
        : `${this.customObjectValue.id}|Search Field Missing`;
    }

    return `${model.id}|${model.title ?? model.name}`;
  }

  private getInitialCOValueData(): CustomObjectValue {
    let result: CustomObjectValue = {};

    if (this.relatedToType) {
      const linkedField = this.getLinkedField();

      result[linkedField.name] = this.getContextCurrentRecordFormat();

      if (linkedField.meta.multi_select) {
        result[linkedField.name] = [result[linkedField.name]];
      }
    }

    return result;
  }

  editCustomObjectValue(event: Event, customObjectValue: CustomObjectValue): void {
    this.store
      .dispatch(new ObjectsManagerActions.GetById(this.customObject.id))
      .pipe(
        untilComponentDestroyed(this),
        switchMap(() =>
          this.store.dispatch(
            new CustomObjectActions.GetValueById({
              id: customObjectValue.id,
              customObjectId: this.customObject.id,
            }),
          ),
        ),
      )
      .subscribe(() => {
        event.preventDefault();
        event.stopPropagation();
        this.customObjectOperation = 'update';
        this.templateId = customObjectValue.template_id;
        this.changeDetectorRef.markForCheck();
        this.matDrawer.toggle();
      });
  }

  deleteCustomObjectValue(event: Event, customObjectValue: any): void {
    event.preventDefault();
    event.stopPropagation();

    this.confirmService
      .openConfirmationModal({
        message: `Are you sure you want to delete this value?`,
      })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this.store.dispatch(new CustomObjectActions.DeleteValue(this.customObjectId, customObjectValue.id));
      });
  }

  lookupField(name: string): FieldDef | undefined {
    return this.fieldDefs.find((fieldDef) => fieldDef.name === name);
  }

  private handleCustomObjectValuesTableColumns(editableColumns: string[]): void {
    if (!this.newTable) {
      const index = editableColumns.indexOf('id');
      if (index !== -1) {
        editableColumns.splice(index, 1);
        editableColumns.unshift('id');
      }
    }
    this.customObjectValuesTableColumns = Array.from(new Set([...editableColumns, 'actions']));

    this.getColDefs();
  }

  private getFieldGroup(field: FieldDef): string {
    if (!this.hasNestedFiltersOn) return field.related_to_type;

    if (field.related_to_type?.startsWith('co_') && field.type === 'record') {
      return field.name.split('.')[0];
    } else {
      return field.related_to_type;
    }
  }

  private captureOriginalStates(): void {
    this.originalStates = {
      displayColumns: this.getDisplayColumnsState(),
      sort: this.getSortState(),
      queryFilters: { ...this.getQueryFiltersState() },
      filterValueList: this.getFilterValueListState(),
    };
  }

  private getSortState(): TableSortState {
    const savedSort = JSON.parse(this.savedView()?.definition)?.table?.sort;

    return {
      active: savedSort?.active || 'created_at',
      direction: savedSort?.direction || 'asc',
    };
  }

  private setSortState(value: TableSortState): void {
    this.originalStates.sort = {
      active: value.active,
      direction: value.direction,
    };
  }

  private getDisplayColumnsState(): string[] {
    const savedFields = removeArchivedFieldsFromTableDefinition(
      JSON.parse(this.savedView().definition)?.table?.fields || [],
      this.fieldDefs,
    );
    return savedFields.length ? savedFields : [...new Set(['id', 'updated_at', 'created_at'])];
  }

  private setDisplayColumnsState(value: string[]) {
    this.originalStates.displayColumns = value;
  }

  private getFilterValueListState(): FilterListItemValueModel[] {
    return this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]');
  }

  private setFilterValueListState(value: FilterListItemValueModel[]) {
    this.originalStates.filterValueList = value;
  }

  private getQueryFiltersState(): Partial<QueryConfig.QueryFilters> {
    return JSON.parse(this.savedFilter?.filters || '{}');
  }

  private setQueryFiltersState(value: Partial<QueryConfig.QueryFilters>) {
    this.originalStates.queryFilters = value;
  }

  private openTrackStateSnackbar(changeType: ChangeType): void {
    this.changeTriggers.add(changeType);
    this.captureOriginalStates();
    this.snackBar.openApplyChanges(
      this.resetToOriginalStates.bind(this),
      this.getManagePublicCustomViewsCustomObjectPermission(this.customObject.name)
        ? this.saveAllChanges.bind(this)
        : null,
    );
  }

  private clearChangeTriggers(): void {
    this.changeTriggers.clear();
  }

  private resetToOriginalStates(): void {
    if (this.changeTriggers.has('columns')) {
      this.displayColumns = this.getDisplayColumnsState();
      this.renderSavedView();
      this.handleCustomObjectValuesTableColumns(this.displayColumns);
      this.closeDisplayFieldsPanel();
    }

    if (this.changeTriggers.has('sort')) {
      this.sortState = this.getSortState();
      this.getCustomObjectValues();
    }

    if (this.changeTriggers.has('filters')) {
      this.queryFilters = this.getQueryFiltersState();
      this.filterValueList.next(this.getFilterValueListState());
      this.getCustomObjectValues();
      this.onCloseByBackdropClick();
    }

    this.clearChangeTriggers();
    this.changeDetectorRef.markForCheck();
  }

  private saveAllChanges(): void {
    if (this.changeTriggers.has('columns')) {
      this.setDisplayColumnsState(this.displayColumns);
      this.saveColumns();
      this.closeDisplayFieldsPanel();
      this.getColDefs();
    }

    if (this.changeTriggers.has('sort')) {
      this.setSortState(this.sortState);
      this.saveTableSort();
    }

    if (this.changeTriggers.has('filters')) {
      this.setQueryFiltersState(this.queryFilters);
      this.setFilterValueListState(this.filterValueList.value);
      this.saveFilter(this.queryFilters);
    }

    this.clearChangeTriggers();
  }

  private closeSnackBarOnLeave(): void {
    this.router.events
      .pipe(
        untilComponentDestroyed(this),
        filter((route) => route instanceof NavigationStart),
      )
      .subscribe(() => this.snackBar.dismiss());
  }

  onAddFilterItem(): void {
    this.filterValueList.next([
      ...this.filterValueList.value,
      {
        fieldName: null,
        operator: null,
        value: null,
      },
    ]);
  }

  onFilterDefChanged(filterDefChangeArgs: FilterListItemChangeArgs): void {
    const index = filterDefChangeArgs.index;
    delete filterDefChangeArgs.index;
    this.filterValueList.value[index] = filterDefChangeArgs;
    this.filterValueList.next([...this.filterValueList.value]);
  }

  onFilterRemoved(index: number): void {
    const newState = this.filterValueList.value?.filter((_, i) => i !== index);
    if (!newState.length) {
      this.onFilterCleared();
    }
    this.filterValueList.next(newState);
  }

  onFilterCleared(): void {
    this.openTrackStateSnackbar('filters');
    this.filterValueList.next([]);
    this.queryFilters = { fjson: `[]`, q: '' };
    this.getCustomObjectValues();
  }

  onFilterApplied(): void {
    this.openTrackStateSnackbar('filters');
    this.queryFilters = {
      fjson: `[${this.filterValueList.value
        .map((x) => `["${x.fieldName}", "${x.operator}", ${JSON.stringify(x.value)}]`)
        .join(', ')}]`,
      q: '',
    };
    this.getCustomObjectValues();
    this.overlayBox.closeOverlay();
  }

  onCloseByBackdropClick(): void {
    this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
  }

  private fjsonStringToFilterValue(fjson: string): FilterListItemValueModel[] {
    return JSON.parse(fjson ?? '[]').map(([fieldName, operator, value]) => ({
      fieldName,
      operator,
      value,
    })) as FilterListItemValueModel[];
  }

  private renderSavedView(): void {
    const parsedFJSON = this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]');
    this.filterValueList.next(parsedFJSON);
    UrlUtils.saveToLocalStorage(this.customObjectSavedViewKey, this.savedView().id, false);
    this.getColDefs();
  }

  onFieldsChanged({ fields }): void {
    const shouldUpdate =
      fields &&
      !isArraysEqual(
        [...fields.filter((item) => item !== 'actions')],
        [...this.displayColumns.filter((item) => item !== 'actions')],
      );

    if (shouldUpdate) {
      this.columnsDebouncer.next(fields);
    }
  }

  private createFirstSavedView(): void {
    this.savedView.set({
      id: null,
      name: `All ${this.customObject.label_plural}`,
      related_to_type: RelatedToType.CustomObject,
      definition: JSON.stringify({ table: { fields: this.displayColumns } }),
    });
    this.accountService.createSavedView(this.savedView()).pipe(untilComponentDestroyed(this)).subscribe(noop);
  }

  onSelectOpenedChanged(opened: boolean): void {
    if (!opened) {
      this.closeDisplayFieldsPanel();
    }
  }

  private saveTableSort(): void {
    this.savedViewUtilsService.saveTableSort(this.savedView, this.sortState);
  }

  private saveColumns(): void {
    if (!this.savedView) {
      this.createFirstSavedView();
    } else {
      this.savedView.update((sv) => {
        return {
          ...sv,
          definition: JSON.stringify({
            table: { fields: this.displayColumns, sort: JSON.parse(sv.definition).table?.sort },
          }),
        };
      });

      this.accountService.updateSavedView(this.savedView()).pipe(untilComponentDestroyed(this)).subscribe(noop);
    }
  }

  dropColumns(event: CdkDragDrop<string[]>): void {
    this.openTrackStateSnackbar('columns');

    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.displayColumns, event.previousIndex, event.currentIndex);
      this.handleCustomObjectValuesTableColumns(this.displayColumns);
    }
  }

  onColumnMoved(event: any): void {
    TsTableService.moveColumns(
      event,
      this.columnDefs.map((x) => x.name.toLowerCase()),
      ['openDetails'],
      (columns: string[]) => {
        this.columnDefs = columns.map((column) => this.lookupField(column));
        this.displayColumns = columns;
        this.handleCustomObjectValuesTableColumns(columns);
        this.openTrackStateSnackbar('columns');
      },
    );
  }

  columnClick(item: any, fieldDef: FieldDef): void {
    if (fieldDef.name.includes('.')) {
      return;
    }

    if (this.isFromRoot) {
      const validRecordTypesToRedirecting = Object.keys(this.customObjectClickActions);
      const isLinkedField = fieldDef.name.startsWith('cf_linked_');
      const id = isLinkedField ? item[fieldDef.name]?.split('|')[0] : undefined;

      if (id && (isLinkedField || validRecordTypesToRedirecting.find((type) => fieldDef.meta.record_type === type))) {
        this.redirectToRecord(fieldDef.meta.record_type, id, item);
      } else {
        this.redirectToObjectDetail(item);
      }
    } else {
      this.router.navigate(['./objects', this.activatedRoute.snapshot.params.id, item.id, 'details']);
    }
  }

  private saveFilter(filter: Partial<QueryConfig.QueryFilters>): void {
    this.queryFilters = filter;
    this.savedView.update((sv) => {
      return {
        ...sv,
        saved_filter: {
          ...sv.saved_filter,
          filters: JSON.stringify(this.queryFilters),
        },
      };
    });
    this.accountService.updateFilter(this.savedView().saved_filter).pipe(untilComponentDestroyed(this)).subscribe(noop);
  }

  private redirectToRecord(recordType: string, id: string, value: any): void {
    const redirectionFunction = this.customObjectClickActions[recordType];

    if (redirectionFunction) {
      redirectionFunction.bind(this)(id);
    } else {
      let customObject: any = this.customObjects.find((x) => x.name == recordType);
      if (customObject) {
        this.router.navigate(['/objects', customObject.id, id, 'details']);
      } else {
        customObject = value.value[this.getLinkedField()?.name];
        this.router.navigate(['/objects', customObject.custom_object_id, customObject.id, 'details']);
      }
    }
  }

  private redirectToProject(id: string): void {
    this.projectService
      .getProjectById(id)
      .pipe(untilComponentDestroyed(this))
      .subscribe((project) => {
        this.router.navigate(['/projects', project.pipeline_id, project.id, 'details', 'overview']);
      });
  }

  private redirectToProperty(id: string): void {
    this.router.navigate(['/properties', id, 'details', 'overview']);
  }

  private redirectToObjectDetail(item: CustomObjectValue): void {
    this.router.navigate(['objects', this.activatedRoute.snapshot.params.id, item.id, 'details']);
  }

  private redirectToCompany(id: string): void {
    this.router.navigate(['companies', id]);
  }

  private redirectToContact(id: string): void {
    this.router.navigate(['contacts', id]);
  }

  private getLinkedField(): FieldDef {
    return (
      this.customObject?.links?.find((x) => x.linked_type === this.relatedToType)?.custom_field ??
      this.store
        .selectSnapshot(ObjectsManagerState.get)
        .find((x) => x.name === this.relatedToType)
        .links.find((x) => x.linked_type === this.customObject.name).custom_field
    );
  }

  private closeDisplayFieldsPanel(): void {
    if (this.fieldDefsPanelOverlayRef?.hasAttached()) {
      this.fieldDefsPanelOverlayRef.detach();
    }

    if (this.fieldsDefsPanelTemplatePortal?.isAttached) {
      this.fieldsDefsPanelTemplatePortal.detach();
    }
  }

  openFieldDefsPanel(): void {
    this.fieldDefsPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.fieldDefsPanelOrigin.viewContainerRef.element.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    this.fieldDefsPanelOverlayRef
      .attachments()
      .pipe(
        untilComponentDestroyed(this),
        switchMap(() => {
          this.fieldDefsPanelOverlayRef.overlayElement.querySelector('div').classList.add('invisible');
          return of(null).pipe(delay(100));
        }),
      )
      .subscribe(() => {
        this.fieldDefsPanelOverlayRef.overlayElement?.querySelector('input')?.focus();
        this.fieldDefsPanelOverlayRef.overlayElement.querySelector('div').classList.add('visible');
      });

    this.fieldsDefsPanelTemplatePortal = new TemplatePortal(this.fieldDefsPanel, this.viewContainerRef);

    this.fieldDefsPanelOverlayRef.attach(this.fieldsDefsPanelTemplatePortal);

    this.fieldDefsPanelOverlayRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.closeDisplayFieldsPanel();
      });

    this.changeDetectorRef.markForCheck();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  exportAsCSV(): void {
    this.getCustomObjectValues(true);
  }

  private refreshRouteData(): void {
    this.data = this.activatedRoute.snapshot.data;
    this.savedView.set(this.activatedRoute.snapshot.data.savedView);
    this.savedFilter = this.savedView().saved_filter;
    this.displayColumns = this.getDisplayColumnsState();
    this.handleCustomObjectValuesTableColumns(this.displayColumns);
    this.queryFilters = this.getQueryFiltersState();
    this.sortState = this.getSortState();
    this.setDefinitionFields();
  }

  private fetchFieldDefs(): void {
    this.fieldDefs = this.store
      .selectSnapshot(
        ObjectsManagerState.getFieldsByObjectName(this.customObject?.name, {
          allowedFieldsOnly: true,
          addNestedFields: this.hasNestedFiltersOn,
        }),
      )
      .map((field) => ({
        ...field,
        group: this.getFieldGroup(field),
      }));

    this.filteredFieldDefs = this.fieldDefs;

    this.setFields();
  }

  private setFields(): void {
    this.typeFields.next(this.tsFilterUtilsService.getFilterList(this.fieldDefs));
  }

  private getMultiSelectFieldValue(customObjectValue: CustomObjectValue): void {
    this.fieldDefs
      .filter(({ name, meta }) => Object.keys(this.customObjectValue).includes(name) && meta?.multi_select)
      .forEach(({ name }) => (this.customObjectValue[name] = customObjectValue[name]));
  }

  private setDefinitionFields(): void {
    if (!this.savedView().definition) {
      this.accountService
        .updateSavedView({
          ...this.savedView(),
          definition: JSON.stringify({ table: { fields: this.displayColumns } }),
        })
        .pipe(untilComponentDestroyed(this))
        .subscribe((savedView) => this.savedView.set(savedView));
    }
  }

  //TS TABLE
  private setColumnsAttributes() {
    this.columnDefs.map((fieldDef: FieldDef) => {
      if (fieldDef) {
        if (fieldDef.meta?.record_type === RelatedToType.User) {
          fieldDef.meta.options = this.store
            //@ts-ignore
            .selectSnapshot(FieldDefsState.selectDropdownOptionsBy('users'))
            .map((x) => ({ id: x.id, label: x.name }));
        }

        fieldDef.tsTableOptions = {
          ...fieldDef.tsTableOptions,
          sortable: true,
          filter: true,
          resizable: true,
          sort: this.sortState.active === fieldDef.name ? this.sortState.direction : null,
          lockPinned: true,
          editable: (params) => {
            return (
              this.getEditCustomObjectPermission(params.colDef.context.related_to_type) &&
              params.data.permissions.is_editable
            );
          },
        };
        this.setTableActions();
      }
    });
  }

  private getColDefs(): void {
    if (this.newTable) {
      this.columnDefs = this.displayColumns.map((column) => this.lookupField(column));
      this.setColumnsAttributes();
    }
  }

  onCellEditingStopped(event) {
    const editedData = event.data;
    this.saveEditedData(editedData);
  }

  private saveEditedData(data: any): void {
    const customObjectValue: CustomObjectValue = {
      related_to_type: this.relatedToType,
      related_to_id: this.relatedToId,
      ...data,
    };

    if (data.id) {
      const id = data.id;
      delete data.id;
      this.store.dispatch(new CustomObjectActions.UpdateValue(this.customObjectId, id, customObjectValue));
    }
  }

  setTableActions(): void {
    this.actions = [
      {
        label: 'Open Details',
        action: (item) => this.columnClick(item, this.lookupField('id')),
        icon: 'space_dashboard',
      },
      {
        label: 'Edit',
        action: (item) => this.editCustomObjectValue(new Event('click'), item),
        icon: 'edit',
        isDisabled: (item) => !item.data.permissions.is_editable,
      },
      {
        label: 'Delete',
        action: (item) => this.deleteCustomObjectValue(new Event('click'), item),
        icon: 'delete',
        isDisabled: (item) => !item.data.permissions.is_deletable,
      },
    ];
  }

  handleOpenDetails(data: any): void {
    this.router.navigate(['/objects', this.activatedRoute.snapshot.params.id, data.id, 'details']);
  }

  private checkTable(): void {
    this.newTable = this.accountService.getCurrentAccount().features.includes(FeatureFlag.NEW_TABLE);
  }

  sortChanged(event: MatSort): void {
    this.sort = event;
    this.sortState = {
      active: this.sort.active,
      direction: this.sort.direction as 'asc' | 'desc',
    };
    this.getCustomObjectValues();
    this.openTrackStateSnackbar('sort');
  }

  onSortChanged(event: SortChangedEvent): void {
    if (event.columns && event.columns.length > 0) {
      const sortedColumn = event.columns.filter((col) => col.getSort());
      this.sortState = {
        active: sortedColumn[0]?.getColDef().field || 'created_at',
        direction: sortedColumn[0]?.getSort() || 'asc',
      };
    } else {
      this.sortState = {
        active: 'created_at',
        direction: 'asc',
      };
    }
    this.getCustomObjectValues(false, 1);
    this.openTrackStateSnackbar('sort');
  }

  getCreateCustomObjectPermission(customObjectName: string): string | boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return PermissionUtils.generateCustomObjectPermissions(customObjectName).CREATE;
    } else {
      return true;
    }
  }

  getEditCustomObjectPermission(customObjectName: string): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      const permission = PermissionUtils.generateCustomObjectPermissions(customObjectName);
      return this.store.selectSnapshot(UserState.hasPermission(permission.EDIT));
    } else {
      return true;
    }
  }

  getDeleteCustomObjectPermission(customObjectName: string): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      const permission = PermissionUtils.generateCustomObjectPermissions(customObjectName);
      return this.store.selectSnapshot(UserState.hasPermission(permission.DELETE));
    } else {
      return true;
    }
  }

  getExportAndShareCustomObjectPermission(customObjectName: string): string | boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return PermissionUtils.generateCustomObjectPermissions(customObjectName).SHARING_RECORDS;
    } else {
      return true;
    }
  }

  getManagePublicCustomViewsCustomObjectPermission(customObjectName: string): string | boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      const permission = PermissionUtils.generateCustomObjectPermissions(customObjectName);
      return this.store.selectSnapshot(UserState.hasPermission(permission.PUBLIC_CUSTOM_VIEWS));
    } else {
      return true;
    }
  }
}
